import React, { useEffect, useState } from "react";
// REDUX
import { useDispatch, useSelector } from "react-redux";
// FLATBUFFER DISPLAY
import { formatUuidT } from "../../flatbuffers/display";
// REDUX SLICES
import { updateTotpTokenIsValid } from "../../redux/authentication/authenticationSlice";
import { updateOpenModalPsw, updateOpenModalOfflineCredential } from "../../redux/users/userSlice";
// API
import { useLazyGetUserPswQuery, usePostUserPswMutation } from "../../redux/login/loginAPI";
import GETJwtToken from "../../redux/authentication/authentication";
// COMPONENTS
import AuthenticationTOTP from "../../pages/authentication/AuthenticationTOTP";
import UsersModalPsw from "../../pages/userManagement/users/UsersModalPsw";
import UsersModalOfflineCredential, {
  OfflineCredentialStatus,
} from "../../pages/userManagement/users/UsersModalOfflineCredential";
import UsersDeleteUserModal from "../../pages/userManagement/users/UsersDeleteUserModal";
import UsersResetPasswordModal from "../../pages/userManagement/users/UsersResetPasswordModal";
import UsersBlockUnBlockModal from "../../pages/userManagement/users/UsersBlockUnBlockModal";
import GlobalAlert2 from "../../pages/home/GlobalAlert2";
// INTERFACES
import { RootState } from "../../app/store";

interface UserCrudSurfaceProps {
  /**
   * Called after a mutation that changes the user list — delete, block/unblock,
   * password reset. Screens that fetch through a LAZY query (the wizard's Users
   * step) have no tag invalidation to ride on, so they pass a reload here.
   * Screens driven by `userRefreshTable` can omit it.
   */
  onUsersChanged?: () => void;
}

/**
 * Every dialog the user actions menu can open, mounted once.
 *
 * This exists so the Users screen under Organization Management and the Users
 * step of the setup wizard are the SAME CRUD, not two that resemble each other.
 * The offline-credential flow in particular used to live inside `UsersTable`
 * — its state, its two API hooks and its TOTP effect — so the wizard could not
 * offer those actions at all without copying the flow, and a copy is how the
 * two screens drift.
 *
 * `pswType` is read from the users slice rather than taken as a prop, because
 * the menu that sets it and this surface are no longer guaranteed to share a
 * React subtree.
 */
function UserCrudSurface({ onUsersChanged = undefined }: UserCrudSurfaceProps) {
  const dispatch = useDispatch();

  const [userPsw, setUserPsw] = useState("");
  const [offlineCredential, setOfflineCredential] = useState<OfflineCredentialStatus | null>(null);

  const { user, openDrawerUser, pswType, userRefreshTable } = useSelector(
    (state: RootState) => state.user,
  );
  const { totpTokenIsValid } = useSelector((state: RootState) => state.authentication);

  const [triggerGetUserPsw, { data: dataUserPsw }] = useLazyGetUserPswQuery();
  const [
    triggerPostUserPsw,
    { data: dataPostUserPsw, isError: isErrorPostUserPsw, error: errorPostUserPsw },
  ] = usePostUserPswMutation();

  const runOfflineCredentialCall = async () => {
    if (!totpTokenIsValid) return;

    const token = await GETJwtToken();
    const rawUserId = user?.id;
    let userId = "";
    if (typeof rawUserId === "string" || typeof rawUserId === "number") {
      userId = String(rawUserId);
    } else if (rawUserId && typeof rawUserId === "object" && !Array.isArray(rawUserId)) {
      userId = formatUuidT(rawUserId as unknown as Parameters<typeof formatUuidT>[0]);
    }
    if (!userId) return;

    if (pswType === "get") {
      triggerGetUserPsw({ token, body: { user_id: userId } });
    } else {
      triggerPostUserPsw({ token, body: { user_id: userId } });
    }
  };

  useEffect(() => {
    if (totpTokenIsValid && !openDrawerUser) {
      runOfflineCredentialCall();
      setTimeout(() => {
        dispatch(updateTotpTokenIsValid(false));
      }, 1000);
    }
  }, [totpTokenIsValid]);

  // The offline-psw GET DESCRIBES the credential; it does not return one. It
  // used to answer with `offline_password`, which was the stored argon2id hash
  // -- unusable, unshareable, and material an attacker can grind offline. The
  // server no longer sends it, so there is nothing here to put in a password
  // dialog.
  useEffect(() => {
    if (dataUserPsw && dataUserPsw.offline_password_set !== undefined) {
      setOfflineCredential(dataUserPsw as OfflineCredentialStatus);
      dispatch(updateOpenModalOfflineCredential(true));
    }
  }, [dataUserPsw]);

  // Issuing a password DOES return one -- this is the only response that ever
  // carries the plaintext, so it is the only one that opens the password
  // dialog. It also means the account was just moved back to OFFLINE_CREATED,
  // so the list's Status column is stale until the caller reloads.
  useEffect(() => {
    const password = dataPostUserPsw?.user_password;
    if (password) {
      dispatch(updateOpenModalPsw(true));
      setUserPsw(password);
      onUsersChanged?.();
    }
  }, [dataPostUserPsw]);

  // Delete, block/unblock and cloud password reset all raise this flag when
  // they succeed. The Organization Management table already refreshes on it;
  // a lazy-query screen needs telling.
  useEffect(() => {
    if (userRefreshTable) {
      onUsersChanged?.();
    }
  }, [userRefreshTable]);

  return (
    <>
      <AuthenticationTOTP />
      <UsersModalPsw userPsw={userPsw} />
      <UsersModalOfflineCredential credential={offlineCredential} />
      <UsersDeleteUserModal />
      <UsersResetPasswordModal />
      <UsersBlockUnBlockModal />
      <GlobalAlert2
        isError={isErrorPostUserPsw}
        isSuccess={false}
        requestType="POST"
        error={errorPostUserPsw}
        name="User"
      />
    </>
  );
}

export default UserCrudSurface;
